<template>
  <div class="mailer-settings">
    <!-- SMTP Configuration Section -->
    <v-card class="mb-6">
      <v-card-title>
        <v-icon start>mdi-email-settings</v-icon>
        SMTP Configuration
      </v-card-title>

      <v-card-text>
        <v-alert type="info" variant="tonal" class="mb-6">
          <strong>Email Server Settings</strong>
          <p class="text-caption mb-0 mt-2">
            Configure your SMTP server settings to enable email functionality. For Gmail, you need to use an App Password.
            <a href="https://myaccount.google.com/apppasswords" target="_blank" class="text-primary">Get Gmail App Password</a>
          </p>
        </v-alert>

        <v-form ref="smtpForm" @submit.prevent="saveSmtpSettings">
          <v-row>
            <!-- SMTP Host -->
            <v-col cols="12" md="8">
              <v-text-field
                v-model="smtpConfig.smtp_host"
                label="SMTP Host"
                variant="outlined"
                density="comfortable"
                placeholder="smtp.gmail.com"
                hint="SMTP server address"
                persistent-hint
                :disabled="savingConfig"
              ></v-text-field>
            </v-col>

            <!-- SMTP Port -->
            <v-col cols="12" md="4">
              <v-text-field
                v-model.number="smtpConfig.smtp_port"
                label="SMTP Port"
                type="number"
                variant="outlined"
                density="comfortable"
                placeholder="587"
                hint="Usually 587 for TLS"
                persistent-hint
                :disabled="savingConfig"
              ></v-text-field>
            </v-col>

            <!-- SMTP User -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model="smtpConfig.smtp_user"
                label="SMTP Username / Email"
                variant="outlined"
                density="comfortable"
                placeholder="your-email@gmail.com"
                hint="Username"
                persistent-hint
                :disabled="savingConfig"
              ></v-text-field>
            </v-col>

            <!-- SMTP Password -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model="smtpConfig.smtp_password"
                label="SMTP Password"
                :type="showPassword ? 'text' : 'password'"
                variant="outlined"
                density="comfortable"
                placeholder="Gmail App Password"
                hint="Password (Use 16 character app-password for Gmail)"
                persistent-hint
                :disabled="savingConfig"
                :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                @click:append-inner="showPassword = !showPassword"
              ></v-text-field>
            </v-col>

            <!-- From Email -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model="smtpConfig.smtp_from_email"
                label="From Email Address"
                variant="outlined"
                density="comfortable"
                placeholder="noreply@spacecoaststem.org"
                hint="Email address shown in 'From' field"
                persistent-hint
                :disabled="savingConfig"
              ></v-text-field>
            </v-col>

            <!-- From Name -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model="smtpConfig.smtp_from_name"
                label="From Name"
                variant="outlined"
                density="comfortable"
                placeholder="Space Coast STEM"
                hint="Sender name shown to recipients"
                persistent-hint
                :disabled="savingConfig"
              ></v-text-field>
            </v-col>

            <!-- Portal URL -->
            <v-col cols="12">
              <v-text-field
                v-model="smtpConfig.portal_url"
                label="Portal URL"
                variant="outlined"
                density="comfortable"
                placeholder="https://portal.spacecoaststem.org"
                hint="Base URL for portal links in emails"
                persistent-hint
                :disabled="savingConfig"
              ></v-text-field>
            </v-col>

            <!-- Organization Address -->
            <v-col cols="12">
              <v-text-field
                v-model="smtpConfig.organization_address"
                label="Organization Physical Address"
                variant="outlined"
                density="comfortable"
                placeholder="123 Main St, City, ST 12345"
                hint="Physical mailing address (required by CAN-SPAM Act for email compliance)"
                persistent-hint
                :disabled="savingConfig"
              ></v-text-field>
            </v-col>

            <!-- Use TLS Checkbox -->
            <v-col cols="12">
              <v-checkbox
                v-model="smtpConfig.smtp_use_tls"
                label="Use TLS/SSL Encryption"
                hint="Recommended: Enable for secure email transmission"
                persistent-hint
                :disabled="savingConfig"
              ></v-checkbox>
            </v-col>
          </v-row>

          <!-- Action Buttons -->
          <div class="d-flex gap-3 mt-4">
            <v-btn
              color="primary"
              type="submit"
              :loading="savingConfig"
              :disabled="!hasChanges"
            >
              <v-icon start>mdi-content-save</v-icon>
              Save Configuration
            </v-btn>

            <v-btn
              color="grey"
              variant="outlined"
              @click="loadSmtpConfig"
              :disabled="savingConfig"
            >
              <v-icon start>mdi-refresh</v-icon>
              Reset
            </v-btn>
          </div>
        </v-form>

        <!-- Success/Error Messages -->
        <v-alert
          v-if="configSuccessMessage"
          type="success"
          variant="tonal"
          closable
          @click:close="configSuccessMessage = ''"
          class="mt-4"
        >
          {{ configSuccessMessage }}
        </v-alert>

        <v-alert
          v-if="configErrorMessage"
          type="error"
          variant="tonal"
          closable
          @click:close="configErrorMessage = ''"
          class="mt-4"
        >
          {{ configErrorMessage }}
        </v-alert>
      </v-card-text>
    </v-card>

    <!-- Email Testing Section (from DebugEmailTester) -->
    <v-card>
      <v-card-title>
        <v-icon start>mdi-email-check</v-icon>
        Email Testing & Preview
      </v-card-title>

      <v-card-text>
        <v-alert type="info" variant="tonal" class="mb-6">
          <strong>Test Email Functionality</strong>
          <p class="text-caption mb-0 mt-2">
            Send test emails to verify your SMTP configuration and preview email templates.
          </p>
        </v-alert>

        <!-- SMTP Test Section -->
        <v-card variant="outlined" class="mb-6">
          <v-card-title class="text-h6">1. Test SMTP Connection</v-card-title>
          <v-card-text>
            <v-text-field
              v-model="testEmail"
              label="Test Email Address"
              type="email"
              variant="outlined"
              density="comfortable"
              hint="Send a test email to verify SMTP configuration"
              persistent-hint
            ></v-text-field>

            <v-btn
              color="primary"
              class="mt-4"
              @click="sendTestEmail"
              :loading="sendingTest"
              :disabled="!testEmail"
            >
              <v-icon start>mdi-send</v-icon>
              Send Test Email
            </v-btn>
          </v-card-text>
        </v-card>

        <!-- Template Testing Section -->
        <v-card variant="outlined" class="mb-6">
          <v-card-title class="text-h6">2. Test Email Templates</v-card-title>
          <v-card-text>
            <v-select
              v-model="selectedTemplate"
              :items="templates"
              item-title="title"
              item-value="value"
              label="Select Template"
              variant="outlined"
              density="comfortable"
              class="mb-4"
            ></v-select>

            <!-- Dynamic form based on selected template -->
            <div v-if="selectedTemplate">
              <v-divider class="mb-4"></v-divider>

              <!-- Teacher Invite Template -->
              <div v-if="selectedTemplate === 'teacher_invite'">
                <v-text-field
                  v-model="templateData.teacher_email"
                  label="Teacher Email"
                  type="email"
                  variant="outlined"
                  density="comfortable"
                  class="mb-3"
                ></v-text-field>
                <v-text-field
                  v-model="templateData.teacher_name"
                  label="Teacher Name"
                  variant="outlined"
                  density="comfortable"
                  class="mb-3"
                ></v-text-field>
                <v-text-field
                  v-model="templateData.invited_by"
                  label="Invited By"
                  variant="outlined"
                  density="comfortable"
                ></v-text-field>
              </div>

              <!-- Student Request - Existing Teacher Template -->
              <div v-if="selectedTemplate === 'student_request_existing'">
                <v-text-field
                  v-model="templateData.teacher_email"
                  label="Teacher Email"
                  type="email"
                  variant="outlined"
                  density="comfortable"
                  class="mb-3"
                ></v-text-field>
                <v-text-field
                  v-model="templateData.teacher_name"
                  label="Teacher Name"
                  variant="outlined"
                  density="comfortable"
                  class="mb-3"
                ></v-text-field>
                <v-text-field
                  v-model="templateData.student_name"
                  label="Student Name"
                  variant="outlined"
                  density="comfortable"
                  class="mb-3"
                ></v-text-field>
                <v-text-field
                  v-model="templateData.equipment_type"
                  label="Equipment Type"
                  variant="outlined"
                  density="comfortable"
                  class="mb-3"
                ></v-text-field>
                <v-text-field
                  v-model="templateData.school_name"
                  label="School Name"
                  variant="outlined"
                  density="comfortable"
                ></v-text-field>
              </div>

              <!-- Student Request - New Teacher Template -->
              <div v-if="selectedTemplate === 'student_request_new'">
                <v-text-field
                  v-model="templateData.teacher_email"
                  label="Teacher Email"
                  type="email"
                  variant="outlined"
                  density="comfortable"
                  class="mb-3"
                ></v-text-field>
                <v-text-field
                  v-model="templateData.teacher_name"
                  label="Teacher Name"
                  variant="outlined"
                  density="comfortable"
                  class="mb-3"
                ></v-text-field>
                <v-text-field
                  v-model="templateData.student_name"
                  label="Student Name"
                  variant="outlined"
                  density="comfortable"
                  class="mb-3"
                ></v-text-field>
                <v-text-field
                  v-model="templateData.equipment_type"
                  label="Equipment Type"
                  variant="outlined"
                  density="comfortable"
                  class="mb-3"
                ></v-text-field>
                <v-text-field
                  v-model="templateData.school_name"
                  label="School Name"
                  variant="outlined"
                  density="comfortable"
                ></v-text-field>
              </div>

              <!-- New Inventory Template -->
              <div v-if="selectedTemplate === 'new_inventory'">
                <v-text-field
                  v-model="templateData.recipient_email"
                  label="Recipient Email"
                  type="email"
                  variant="outlined"
                  density="comfortable"
                  class="mb-3"
                ></v-text-field>
                <v-text-field
                  v-model="templateData.equipment_type"
                  label="Equipment Type"
                  variant="outlined"
                  density="comfortable"
                  class="mb-3"
                ></v-text-field>
                <v-text-field
                  v-model.number="templateData.quantity"
                  label="Quantity"
                  type="number"
                  variant="outlined"
                  density="comfortable"
                ></v-text-field>
              </div>

              <!-- Request Filled Template -->
              <div v-if="selectedTemplate === 'request_filled'">
                <v-text-field
                  v-model="templateData.requester_email"
                  label="Requester Email"
                  type="email"
                  variant="outlined"
                  density="comfortable"
                  class="mb-3"
                ></v-text-field>
                <v-text-field
                  v-model="templateData.requester_name"
                  label="Requester Name"
                  variant="outlined"
                  density="comfortable"
                  class="mb-3"
                ></v-text-field>
                <v-text-field
                  v-model="templateData.student_name"
                  label="Student Name"
                  variant="outlined"
                  density="comfortable"
                  class="mb-3"
                ></v-text-field>
                <v-text-field
                  v-model="templateData.equipment_type"
                  label="Equipment Type"
                  variant="outlined"
                  density="comfortable"
                  class="mb-3"
                ></v-text-field>
                <v-text-field
                  v-model="templateData.equipment_details"
                  label="Equipment Details"
                  variant="outlined"
                  density="comfortable"
                  class="mb-3"
                ></v-text-field>
                <v-text-field
                  v-model="templateData.asset_tag"
                  label="Asset Tag"
                  variant="outlined"
                  density="comfortable"
                  class="mb-3"
                ></v-text-field>
                <v-textarea
                  v-model="templateData.pickup_instructions"
                  label="Pickup Instructions"
                  variant="outlined"
                  rows="3"
                  class="mb-3"
                ></v-textarea>
                <v-text-field
                  v-model="templateData.contact_info"
                  label="Contact Info"
                  variant="outlined"
                  density="comfortable"
                ></v-text-field>
              </div>

              <div class="d-flex gap-3 mt-4">
                <v-btn
                  color="primary"
                  @click="previewTemplate"
                  :disabled="!selectedTemplate"
                  variant="outlined"
                >
                  <v-icon start>mdi-eye</v-icon>
                  Preview in New Tab
                </v-btn>

                <v-btn
                  color="success"
                  @click="sendTemplateTest"
                  :loading="sendingTemplate"
                  :disabled="!canSendTemplate"
                >
                  <v-icon start>mdi-email-send</v-icon>
                  Send Template Test
                </v-btn>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Result Messages -->
        <v-alert
          v-if="successMessage"
          type="success"
          variant="tonal"
          closable
          @click:close="successMessage = ''"
          class="mb-4"
        >
          {{ successMessage }}
        </v-alert>

        <v-alert
          v-if="errorMessage"
          type="error"
          variant="tonal"
          closable
          @click:close="errorMessage = ''"
        >
          {{ errorMessage }}
        </v-alert>
      </v-card-text>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { api } from '@/composables/api'

// SMTP Configuration State
const smtpConfig = ref({
  smtp_host: 'smtp.gmail.com',
  smtp_port: 587,
  smtp_user: '',
  smtp_password: '',
  smtp_from_email: 'noreply@spacecoaststem.org',
  smtp_from_name: 'Space Coast STEM',
  smtp_use_tls: true,
  portal_url: 'https://portal.spacecoaststem.org',
  organization_address: ''
})

const originalSmtpConfig = ref({})
const showPassword = ref(false)
const savingConfig = ref(false)
const loadingConfig = ref(false)
const configSuccessMessage = ref('')
const configErrorMessage = ref('')

// Email Testing State
const testEmail = ref('')
const sendingTest = ref(false)
const selectedTemplate = ref('')
const sendingTemplate = ref(false)
const successMessage = ref('')
const errorMessage = ref('')

const templates = [
  { title: 'Teacher Invite', value: 'teacher_invite' },
  { title: 'Student Request (Existing Teacher)', value: 'student_request_existing' },
  { title: 'Student Request (New Teacher)', value: 'student_request_new' },
  { title: 'New Inventory Available', value: 'new_inventory' },
  { title: 'Request Filled Notification', value: 'request_filled' },
  { title: 'New User Admin Notification', value: 'new_user_admin_notification' },
  { title: 'User Account Approved', value: 'user_account_approved' }
]

const templateData = ref<any>({
  // Common fields
  teacher_email: '',
  teacher_name: 'Jane Smith',
  student_name: 'John Doe',
  equipment_type: 'Laptop',
  school_name: 'Sample High School',
  invited_by: 'Admin User',

  // New inventory
  recipient_email: '',
  quantity: 5,

  // Request filled
  requester_email: '',
  requester_name: 'Parent Name',
  equipment_details: 'Dell Latitude 5490, Intel Core i5, 8GB RAM, 256GB SSD',
  asset_tag: 'SCT-2024-001',
  pickup_instructions: 'Equipment can be picked up at the school office during regular hours (8 AM - 3 PM, Monday-Friday). Please bring this email and a valid ID.',
  contact_info: 'If you have questions, please contact us at info@spacecoaststem.org or call (321) 555-0100.'
})

const hasChanges = computed(() => {
  return JSON.stringify(smtpConfig.value) !== JSON.stringify(originalSmtpConfig.value)
})

const canSendTemplate = computed(() => {
  if (!selectedTemplate.value) return false

  switch (selectedTemplate.value) {
    case 'teacher_invite':
      return !!templateData.value.teacher_email && !!templateData.value.teacher_name
    case 'student_request_existing':
    case 'student_request_new':
      return !!templateData.value.teacher_email &&
             !!templateData.value.teacher_name &&
             !!templateData.value.student_name &&
             !!templateData.value.equipment_type
    case 'new_inventory':
      return !!templateData.value.recipient_email &&
             !!templateData.value.equipment_type &&
             templateData.value.quantity > 0
    case 'request_filled':
      return !!templateData.value.requester_email &&
             !!templateData.value.requester_name &&
             !!templateData.value.student_name
    default:
      return false
  }
})

// Load SMTP configuration
const loadSmtpConfig = async () => {
  loadingConfig.value = true
  configErrorMessage.value = ''
  configSuccessMessage.value = ''

  try {
    const response = await api.get('/configuration')
    const config = response.data

    smtpConfig.value = {
      smtp_host: config.smtp_host || 'smtp.gmail.com',
      smtp_port: config.smtp_port || 587,
      smtp_user: config.smtp_user || '',
      smtp_password: config.smtp_password || '',
      smtp_from_email: config.smtp_from_email || 'noreply@spacecoaststem.org',
      smtp_from_name: config.smtp_from_name || 'Space Coast STEM',
      smtp_use_tls: config.smtp_use_tls !== false,
      portal_url: config.portal_url || 'https://portal.spacecoaststem.org',
      organization_address: config.organization_address || ''
    }

    originalSmtpConfig.value = JSON.parse(JSON.stringify(smtpConfig.value))
  } catch (error: any) {
    console.error('Error loading SMTP config:', error)
    configErrorMessage.value = 'Failed to load SMTP configuration'
  } finally {
    loadingConfig.value = false
  }
}

// Save SMTP configuration
const saveSmtpSettings = async () => {
  savingConfig.value = true
  configErrorMessage.value = ''
  configSuccessMessage.value = ''

  try {
    await api.put('/configuration', smtpConfig.value)

    originalSmtpConfig.value = JSON.parse(JSON.stringify(smtpConfig.value))
    configSuccessMessage.value = 'SMTP configuration saved successfully!'
  } catch (error: any) {
    console.error('Error saving SMTP config:', error)
    configErrorMessage.value = error.response?.data?.detail || 'Failed to save SMTP configuration'
  } finally {
    savingConfig.value = false
  }
}

// Send test email
const sendTestEmail = async () => {
  sendingTest.value = true
  errorMessage.value = ''
  successMessage.value = ''

  try {
    const response = await api.post('/admin/debug/test-email', {
      to_email: testEmail.value
    })

    if (response.data?.success) {
      successMessage.value = `Test email sent successfully to ${testEmail.value}!`
    } else {
      errorMessage.value = response.data?.message || 'Failed to send test email'
    }
  } catch (error: any) {
    console.error('Error sending test email:', error)
    errorMessage.value = error.response?.data?.detail || 'Failed to send test email. Check SMTP configuration.'
  } finally {
    sendingTest.value = false
  }
}

// Send template test
const sendTemplateTest = async () => {
  sendingTemplate.value = true
  errorMessage.value = ''
  successMessage.value = ''

  try {
    const response = await api.post('/admin/debug/test-email-template', {
      template_type: selectedTemplate.value,
      data: templateData.value
    })

    if (response.data?.success) {
      successMessage.value = `Template email sent successfully!`
    } else {
      errorMessage.value = response.data?.message || 'Failed to send template email'
    }
  } catch (error: any) {
    console.error('Error sending template email:', error)
    errorMessage.value = error.response?.data?.detail || 'Failed to send template email. Check configuration.'
  } finally {
    sendingTemplate.value = false
  }
}

// Preview template
const previewTemplate = async () => {
  errorMessage.value = ''

  try {
    const response = await api.post('/admin/debug/preview-email-template', {
      template_type: selectedTemplate.value,
      data: templateData.value
    }, {
      headers: {
        'Accept': 'text/html'
      }
    })

    // Open the HTML in a new window
    const previewWindow = window.open('', '_blank')
    if (previewWindow) {
      previewWindow.document.write(response.data)
      previewWindow.document.close()
    } else {
      errorMessage.value = 'Please allow popups to preview emails.'
    }
  } catch (error: any) {
    console.error('Error previewing template:', error)
    errorMessage.value = error.response?.data?.detail || 'Failed to preview template. Please try again.'
  }
}

onMounted(() => {
  loadSmtpConfig()
})
</script>

<style scoped>
.mailer-settings {
  max-width: 1200px;
}

.text-caption a {
  text-decoration: underline;
}
</style>
